import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { Service } from '../../data/servicesData';
import { wizardQuestions, WizardQuestion, WizardOption, getTotalSteps } from '../../data/wizardQuestions';

// Define the type for storing answers
type Answers = Record<string, string | number | boolean | string[]>; // Allow string arrays for multi-select

interface ServiceWizardProps {
  services: Service[]; // Assuming this is still needed for final recommendations
  onServiceSelect: (service: Service) => void;
  isOpen: boolean; // Control modal visibility
  onClose: () => void; // Handle modal close
}

const INITIAL_STEP_ID = 'step1_age'; // Back to original start

const ServiceWizard: React.FC<ServiceWizardProps> = ({ services, onServiceSelect, isOpen, onClose }) => {
  const [currentStepId, setCurrentStepId] = useState<string>(INITIAL_STEP_ID);
  const [answers, setAnswers] = useState<Answers>({});
  const [history, setHistory] = useState<string[]>([]);
  const [answeredStep4Priorities, setAnsweredStep4Priorities] = useState<string[]>([]); // For Step 4 (Specific Needs)
  const [recommendedServices, setRecommendedServices] = useState<Service[]>([]);
  
  // State specifically for the results page form
  const [resultsName, setResultsName] = useState<string>('');
  const [resultsPhone, setResultsPhone] = useState<string>('');
  const [resultsEmail, setResultsEmail] = useState<string>(''); 

  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');


  const currentQuestion: WizardQuestion | undefined = wizardQuestions[currentStepId];
  const totalSteps = useMemo(() => getTotalSteps(), []); // Should be 5

  // --- Navigation Logic ---

  // Removed handleContactSubmit

  const handleOptionSelect = useCallback((option: WizardOption) => {
    if (!currentQuestion) return; // Removed isContactStep check

    const questionId = currentStepId;
    const optionValue = option.value as string;

    if (currentQuestion.isMultiSelect) {
      // --- Multi-select Logic ---
      const currentSelection = (answers[questionId] as string[] | undefined) || [];
      let newSelection: string[];

      if (currentSelection.includes(optionValue)) {
        newSelection = currentSelection.filter(val => val !== optionValue);
      } else {
        if (currentQuestion.maxSelections && currentSelection.length >= currentQuestion.maxSelections) {
          // console.log(`Max selections (${currentQuestion.maxSelections}) reached.`);
          return;
        }
        newSelection = [...currentSelection, optionValue];
      }
      setAnswers(prev => ({ ...prev, [questionId]: newSelection }));

    } else {
      // --- Single-select Logic ---
      const currentAnswer = option.value;
      const nextAnswers = { ...answers, [questionId]: currentAnswer };
      setAnswers(nextAnswers);
      setHistory(prev => [...prev, questionId]);
      let nextStep = option.nextStepId;

      // ** Conditional Logic: Step 2 (Work & Finances) - Business Owner ** 
      if (questionId === 'step2_employment' && option.value === 'business_owner') {
        nextStep = 'step2_employees';
      }

      navigateToStep(nextStep, nextAnswers);
    }

  }, [currentStepId, answers, currentQuestion]);

  // Define the order in which to check Step 4 priorities (Specific Needs)
  const step4PriorityOrder = [ 
    'protection',
    'savings',
    'business',
    'retirement_income',
    'estate',
    'advanced',
    'debt_manage',
    'short_term'
  ];

  // Helper function to find the next relevant Step 4 question ID 
  const findNextStep4QuestionId = (answeredPriorities: string[], selectedPriorities: string[]): string | null => { 
    for (const priority of step4PriorityOrder) { 
      if (selectedPriorities.includes(priority) && !answeredPriorities.includes(priority)) {
        // Find the first question associated with this priority in Step 4
        const firstQuestionForPriority = Object.values(wizardQuestions).find(
          q => q.stepNumber === 4 && q.dependsOnPriority === priority // Check stepNumber 4
        );
        if (firstQuestionForPriority) {
          return firstQuestionForPriority.id; 
        }
      }
    }
    return null; // No more relevant Step 4 questions found
  };

  // Separate navigation function
  const navigateToStep = (nextStepId: string, currentAnswers: Answers) => {
     let targetStepId = nextStepId;

     // --- Step 3 (Priorities) to Step 4 (Needs) Jump Logic --- 
     if (currentStepId === 'step4_priorities') { // This ID is the last question of Step 3
       const selectedPriorities = (currentAnswers['step4_priorities'] as string[] | undefined) || [];
       const firstStep4Id = findNextStep4QuestionId([], selectedPriorities); 

       if (firstStep4Id) {
         targetStepId = firstStep4Id; // Go to the first question of Step 4
       } else {
         targetStepId = 'results'; // No relevant Step 4 questions, go to results
       }
     }

     // --- Step 4 Inter-Priority Jump Logic --- 
     if (targetStepId === 'step5_needs_start' && currentQuestion?.dependsOnPriority) { // step5_needs_start is the logical jump point ID
        const justFinishedPriority = currentQuestion.dependsOnPriority;
        const newlyAnsweredPriorities = [...answeredStep4Priorities, justFinishedPriority]; 
        setAnsweredStep4Priorities(newlyAnsweredPriorities); 

        const selectedPriorities = (currentAnswers['step4_priorities'] as string[] | undefined) || []; // Priorities are answered in step 3
        const nextStep4Id = findNextStep4QuestionId(newlyAnsweredPriorities, selectedPriorities); 

        if (nextStep4Id) {
          targetStepId = nextStep4Id; 
        } else {
          targetStepId = 'results'; // All relevant Step 4 questions done
        }
     }

     // --- Navigate or Show Results ---
     if (targetStepId === 'results') {
       // console.log("Reached results step. Final Answers:", currentAnswers);
       const recommendations = generateRecommendations(currentAnswers, services); 
       setRecommendedServices(recommendations);
       setCurrentStepId('results');
     } else if (wizardQuestions[targetStepId]) {
       setCurrentStepId(targetStepId);
     } else {
       if (import.meta.env.DEV) {

         if (import.meta.env.DEV) {
           console.warn(`Invalid targetStepId: ${targetStepId} determined from step ${currentStepId}`);
         }

       }
       setCurrentStepId('results'); // Fallback
     }
  };

  // Handler for the "Next" button on multi-select steps
  const handleNextMultiSelect = useCallback(() => {
    if (!currentQuestion || !currentQuestion.isMultiSelect) return;
    setHistory(prev => [...prev, currentStepId]);
    const nextStepId = currentQuestion.options[0]?.nextStepId || 'results';
    navigateToStep(nextStepId, answers);
  }, [currentStepId, currentQuestion, answers, navigateToStep]);


  const handleBack = useCallback(() => {
    if (history.length === 0) return; 
    const previousStepId = history[history.length - 1];
    const newAnswers = { ...answers };
    delete newAnswers[currentStepId]; // Clear answer for the step we are leaving
    
    // If going back within Step 4, remove the last answered priority
    const leavingQuestion = wizardQuestions[currentStepId];
    if (leavingQuestion?.stepNumber === 4 && leavingQuestion?.dependsOnPriority) {
        const lastAnswered = answeredStep4Priorities[answeredStep4Priorities.length - 1];
        if (lastAnswered === leavingQuestion.dependsOnPriority) {
            setAnsweredStep4Priorities(prev => prev.slice(0, -1));
        }
    }

    setAnswers(newAnswers);
    setHistory(prev => prev.slice(0, -1));
    setCurrentStepId(previousStepId);

  }, [history, answers, currentStepId, answeredStep4Priorities]); // Added answeredStep4Priorities dependency


  // --- Select/Deselect All Handlers ---
  const handleSelectAll = useCallback(() => {
    if (!currentQuestion || !currentQuestion.isMultiSelect || currentQuestion.maxSelections) return;
    const allValues = currentQuestion.options.map(opt => opt.value as string);
    setAnswers(prev => ({ ...prev, [currentStepId]: allValues }));
  }, [currentQuestion, currentStepId]);

  const handleDeselectAll = useCallback(() => {
    if (!currentQuestion || !currentQuestion.isMultiSelect) return; // Can deselect even if maxSelections exists
    setAnswers(prev => ({ ...prev, [currentStepId]: [] }));
  }, [currentQuestion, currentStepId]);


  // --- Recommendation Generation Function ---
  // Uses original step IDs for answers
  const generateRecommendations = useCallback((finalAnswers: Answers, allServices: Service[]): Service[] => {
    const recs: Service[] = [];
    const priorities = (finalAnswers['step4_priorities'] as string[] | undefined) || []; // Priorities from Step 3
    const employmentStatus = finalAnswers['step2_employment'] as string; // Step 2
    const dependents = finalAnswers['step1_dependents'] as string; // Step 1
    const goals = (finalAnswers['step3_goals'] as string[] | undefined) || []; // Step 3
    const ageRange = finalAnswers['step1_age'] as string; // Step 1

    const addRec = (serviceId: string) => {
      const service = allServices.find(s => s.id === serviceId);
      if (service && !recs.some(r => r.id === serviceId)) {
        recs.push(service);
      }
    };

    // --- Rules referencing Step 4 answers ---
    // (Keep original IDs like 'step5_protection_existing_life' as they haven't changed, only stepNumber)
    
    // Rule 1: Protection
    if (priorities.includes('protection')) {
      if ((dependents === 'children_under_18' || dependents === 'other_dependents') && finalAnswers['step5_protection_existing_life'] !== true) {
        addRec('term-life-insurance');
        addRec('whole-life-insurance');
      }
      if (!['retired', 'student', 'unemployed'].includes(employmentStatus) && finalAnswers['step5_protection_existing_ci_di'] !== true) {
        addRec('disability-insurance');
        addRec('critical-illness-insurance');
      }
      if (finalAnswers['step5_protection_debt_concern'] === true) {
          addRec('term-life-insurance');
          addRec('disability-insurance');
      }
    }

    // Rule 2: Savings/Retirement
    if (priorities.includes('savings') && goals.includes('retirement')) {
        addRec('rrsp');
        addRec('tfsa');
    }
      if (priorities.includes('savings') && ['low', 'very_low'].includes(finalAnswers['step5_savings_risk_tolerance'] as string) && (goals.includes('emergency_fund') || finalAnswers['step5_short_term_purpose'] === 'emergency')) {
        addRec('hisa');
    }
      if (priorities.includes('retirement_income') && ['55_64', '65_plus'].includes(ageRange) && finalAnswers['step5_retirement_income_goal'] === 'guaranteed_income') {
        addRec('annuities');
        addRec('rrif');
    } else if (priorities.includes('retirement_income') && ['55_64', '65_plus'].includes(ageRange)) {
         addRec('rrif');
    }
    
    // Rule 3: Business
    if (priorities.includes('business') && employmentStatus === 'business_owner') {
        if (finalAnswers['step5_business_objective'] === 'succession' || finalAnswers['step5_business_objective'] === 'owner_retirement') {
          addRec('buy-sell-agreement-funding');
          addRec('insured-retirement-strategy');
        }
        if (finalAnswers['step5_business_key_person'] === true) {
            addRec('key-person-insurance');
        }
        // Employee count is answered in step 2
        if (finalAnswers['step5_business_objective'] === 'benefits' && finalAnswers['step2_employees'] !== '1') { 
            addRec('group-benefits-policies');
        }
    }
    
    // Rule 4: Estate/Legacy
    if (priorities.includes('estate')) {
        if (finalAnswers['step5_estate_legacy_interest'] === true) {
            addRec('charitable-legacy-planning');
            addRec('estate-preservation');
        }
        if (finalAnswers['step5_estate_tax_concern'] === true) {
            addRec('estate-preservation');
            addRec('participating-whole-life-insurance');
            addRec('universal-life-insurance');
            addRec('whole-life-insurance');
        }
        if (finalAnswers['step5_estate_legacy_interest'] !== true && finalAnswers['step5_estate_tax_concern'] !== true) {
             addRec('estate-preservation');
        }
    }

    // Rule 5: Advanced Wealth
      if (priorities.includes('advanced')) {
          if (finalAnswers['step5_advanced_corp_assets'] === true) {
              addRec('ifa-strategy');
              addRec('corporate-owned-life-insurance');
          }
          if (finalAnswers['step5_advanced_min_tax'] === true) {
              addRec('giant-tfsa-tax-exempt-policies');
              addRec('participating-whole-life-insurance');
          }
           if (finalAnswers['step5_advanced_leveraged'] === true || finalAnswers['step5_advanced_leveraged'] === 'tell_more') {
              addRec('insured-retirement-strategy');
              addRec('ifa-strategy');
           }
           addRec('giant-tfsa-tax-exempt-policies');
           addRec('insured-retirement-strategy');
           addRec('corporate-owned-life-insurance');
      }
      
      // Rule 6: Short-term Savings
      if (priorities.includes('short_term')) {
          addRec('hisa');
      }
      
    // console.log("Generated Recommendations:", recs.map(r => r.id));
    return recs;
  }, [services]);


  const handleEmailSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    // Get contact info from the results page form state
    if (!resultsEmail || !resultsName || isSubmitting) { // Name is also required now
        // console.log("Name and Email are required on results page.");
        // Optional: Add user feedback here
        return;
    }

    setIsSubmitting(true);
    setSubmitStatus('idle');
    // console.log("Submitting results for:", resultsEmail);
    // console.log("Name:", resultsName);
    // console.log("Phone:", resultsPhone);
    if (import.meta.env.DEV) {
      console.log("Answers:", answers);
    }
    // console.log("Recommendations:", recommendedServices.map(s => s.id));

    // Construct payload using data from results form state and answers
    const payload = {
      userEmail: resultsEmail,
      userName: resultsName, 
      userPhone: resultsPhone, 
      answers, // Send all answers from the wizard steps
      recommendedServiceIds: recommendedServices.map(s => s.id)
    };

    try {
      const response = await fetch('/api/send-wizard-results', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
         const errorData = await response.json();
         if (import.meta.env.DEV) {

           if (import.meta.env.DEV) {
             console.error("API Error:", errorData);
           }

         }
         throw new Error(errorData.message || 'Failed to send results');
      } else {
        setSubmitStatus('success');
      }

    } catch (error) {
      if (import.meta.env.DEV) {

        if (import.meta.env.DEV) {
          console.error("Error submitting wizard results:", error);
        }

      }
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  // Dependencies now include results form state
  }, [resultsEmail, resultsName, resultsPhone, answers, recommendedServices, isSubmitting]);


  const handleReset = useCallback(() => {
    setCurrentStepId(INITIAL_STEP_ID);
    setAnswers({});
    setHistory([]);
    setAnsweredStep4Priorities([]); // Reset Step 4 priority tracking
    setRecommendedServices([]);
    // Reset results page form state
    setResultsName('');
    setResultsPhone('');
    setResultsEmail('');
    // Reset results page status
    setSubmitStatus('idle');
    setIsSubmitting(false);
    // Removed contactError reset
  }, []);

  // --- Rendering Logic ---

  const renderProgress = () => {
    if (!currentQuestion) return null;
    const currentStepNum = currentQuestion.stepNumber;
    const stepLabels = [ // Back to 5 steps
      'About You',
      'Work & Finances',
      'Assets, Goals & Priorities',
      'Specific Needs',
      'Results'
    ];

    return (
      <div className="mb-8 px-2"> 
        <div className="flex items-center justify-between mb-2">
          {stepLabels.map((label, index) => {
            const stepNum = index + 1;
            const isCompleted = stepNum < currentStepNum;
            const isActive = stepNum === currentStepNum;

            return (
              <div key={stepNum} className={`flex-1 text-center ${isActive ? 'font-semibold text-blue-600' : isCompleted ? 'text-gray-600' : 'text-gray-400'}`}>
                <span className="text-xs md:text-sm block">{label}</span>
              </div>
            );
          })}
        </div>
        <div className="relative pt-1">
          <div className="overflow-hidden h-2 mb-4 text-xs flex rounded bg-gray-200">
            {stepLabels.map((_, index) => {
              const stepNum = index + 1;
              const isFilled = stepNum <= currentStepNum;
              return (
                <div
                  key={stepNum}
                  style={{ width: `${100 / totalSteps}%` }}
                  className={`shadow-none flex flex-col text-center whitespace-nowrap text-white justify-center ${isFilled ? 'bg-blue-500' : 'bg-gray-200'} transition-colors duration-300 ease-in-out`}
                ></div>
              );
            })}
          </div>
        </div>
      </div>
    );
  };

  // Removed renderContactStep

  const renderQuestion = () => {
    // Removed check for contact step
    if (!currentQuestion || currentStepId === 'results') return null;

    const isMulti = currentQuestion.isMultiSelect;
    const currentSelections = (answers[currentStepId] as string[] | undefined) || [];

    return (
      <div className="space-y-6">
        <h3 className="text-xl font-semibold text-gray-800 mb-2">{currentQuestion.text}</h3> {/* Reduced bottom margin */}
        
        {/* Instructions and Select/Deselect All Buttons */}
        <div className="flex justify-between items-center mb-4">
          <div>
            {isMulti && currentQuestion.maxSelections && (
              <p className="text-sm text-gray-500">Select up to {currentQuestion.maxSelections}.</p>
            )}
            {isMulti && !currentQuestion.maxSelections && (
              <p className="text-sm text-gray-500">Select all that apply.</p>
            )}
          </div>
          {/* Add Select All / Deselect All buttons for multi-select without limit */}
          {isMulti && !currentQuestion.maxSelections && (
            <div className="space-x-2">
              <button
                onClick={handleSelectAll}
                className="text-xs px-2 py-1 border border-gray-300 rounded text-gray-600 hover:bg-gray-100"
              >
                Select All
              </button>
              <button
                onClick={handleDeselectAll}
                disabled={currentSelections.length === 0}
                className="text-xs px-2 py-1 border border-gray-300 rounded text-gray-600 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Deselect All
              </button>
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {currentQuestion.options.map((option) => {
            const isSelected = isMulti && currentSelections.includes(option.value as string);
            return (
              <button
                key={option.value.toString()}
                onClick={() => handleOptionSelect(option)}
                className={`p-4 border rounded-lg text-left transition-all focus:outline-none focus:ring-2 focus:ring-blue-500 shadow-sm ${
                  isSelected
                    ? 'bg-blue-100 border-blue-400 ring-2 ring-blue-300'
                    : 'bg-white border-gray-200 hover:bg-gray-50 hover:border-blue-300'
                }`}
              >
                <span className="block text-gray-800 font-medium">{option.text}</span>
              </button>
            );
          })}
        </div>
        {/* Conditionally render Next button for multi-select */}
        {isMulti && (
           <div className="flex justify-end pt-4">
             <button
               onClick={handleNextMultiSelect}
               disabled={currentSelections.length === 0}
               className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
             >
               Next
             </button>
           </div>
        )}
      </div>
    );
  };

  const renderResults = () => {
    if (currentStepId !== 'results') return null;

    // Removed finalEmail variable, use resultsEmail directly

    return (
      <div className="space-y-8">
        <div>
          <h3 className="text-xl font-semibold text-gray-800 mb-2">Your Financial Compass Results</h3>
          <p className="text-gray-600 mb-4">Based on your answers, here are some services that might be relevant to your priorities:</p>

        {recommendedServices.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {recommendedServices.map((service) => (
              <div
                key={service.id}
                className="p-4 bg-white border border-gray-200 rounded-lg shadow-sm hover:shadow-md transition-all cursor-pointer"
                onClick={() => onServiceSelect(service)}
              >
                <h4 className="text-lg font-medium text-gray-800 mb-2">{service.title}</h4>
                <p className="text-gray-600 text-sm line-clamp-3">{service.description}</p>
                <div className="mt-3 text-blue-600 text-sm font-medium flex items-center">
                  <span>Learn more</span>
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 ml-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-8">
            <p className="text-gray-500">Based on your selections, we recommend a personalized consultation to best address your needs.</p>
          </div>
        )}
        </div>

        {/* Contact Form & CTA Section */}
        <div className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm">
          <h4 className="text-lg font-semibold text-gray-800 mb-3">Next Steps</h4>

           <a
             href="https://calendly.com/chet-paslawski/pcm-financial-discovery-call"
             target="_blank"
             rel="noopener noreferrer"
             className="block w-full mb-4 px-6 py-3 bg-blue-600 text-white text-center font-medium rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition duration-150 ease-in-out"
           >
             Schedule a No-Obligation Consultation
           </a>

          <p className="text-sm text-gray-600 mb-4 text-center">Or, get a copy of your results and connect with an advisor:</p>

          {/* Contact Form Integrated into Results */}
          <form onSubmit={handleEmailSubmit} className="space-y-4">
             <div>
               <label htmlFor="results-name" className="block text-sm font-medium text-gray-700 mb-1">Name</label>
               <input
                 type="text"
                 id="results-name"
                 name="results-name"
                 value={resultsName}
                 onChange={(e) => setResultsName(e.target.value)}
                 required
                 className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 bg-white text-gray-900"
               />
             </div>
             <div>
               <label htmlFor="results-email" className="block text-sm font-medium text-gray-700 mb-1">Email</label>
               <input
                 type="email"
                 id="results-email" 
                 name="results-email"
                 value={resultsEmail} 
                 onChange={(e) => setResultsEmail(e.target.value)} 
                 placeholder="Enter your email address"
                 required
                 className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 bg-white text-gray-900"
                 disabled={isSubmitting || submitStatus === 'success'}
               />
             </div>
              <div>
               <label htmlFor="results-phone" className="block text-sm font-medium text-gray-700 mb-1">Phone (Optional)</label>
               <input
                 type="tel"
                 id="results-phone"
                 name="results-phone"
                 value={resultsPhone}
                 onChange={(e) => setResultsPhone(e.target.value)}
                 className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 bg-white text-gray-900"
               />
             </div>
            <button
              type="submit"
              disabled={isSubmitting || submitStatus === 'success' || !resultsEmail || !resultsName} // Disable if no name/email
              className="w-full px-6 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 disabled:opacity-60 disabled:cursor-not-allowed transition duration-150 ease-in-out"
            >
              {isSubmitting ? 'Sending...' : submitStatus === 'success' ? 'Results Sent!' : 'Send My Results & Connect'}
            </button>
            {submitStatus === 'error' && (
              <p className="text-red-600 text-sm mt-2 text-center">Could not send results. Please try again later or contact us directly.</p>
            )}
             {submitStatus === 'success' && (
              <p className="text-green-600 text-sm mt-2 text-center">Your results have been sent!</p>
            )}
          </form>
        </div>
      </div>
    );
  };

  // Handle escape key press to close modal
  useEffect(() => {
    const handleEscapeKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };
    
    window.addEventListener('keydown', handleEscapeKey);
    return () => window.removeEventListener('keydown', handleEscapeKey);
  }, [isOpen, onClose]);

  // Prevent body scrolling when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto" aria-labelledby="modal-title" role="dialog" aria-modal="true">
      {/* Overlay */}
      <div 
        className="fixed inset-0 bg-gray-900 bg-opacity-75 transition-opacity"
        aria-hidden="true"
        onClick={onClose}
      ></div>
      
      {/* Modal container */}
      <div className="flex items-center justify-center min-h-screen p-4">
        {/* Modal content */}
        <div className="bg-gray-50 rounded-xl shadow-xl overflow-hidden w-full max-w-4xl relative z-10 max-h-[90vh] overflow-y-auto">
          {/* Close button */}
          <button 
            onClick={onClose}
            className="absolute top-4 right-4 text-gray-500 hover:text-gray-700 focus:outline-none z-20"
            aria-label="Close modal"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
          
          <div className="p-6 md:p-8">
            <h2 className="text-2xl font-bold text-gray-800 mb-2 text-center">Financial Compass</h2>

        {renderProgress()}

        {currentStepId === 'results' ? renderResults() : renderQuestion()}

        {/* Navigation Buttons - Show Back always except first step */}
        <div className={`mt-8 flex ${currentQuestion?.isMultiSelect && currentStepId !== 'results' ? 'justify-start' : 'justify-between'} items-center`}> 
          {currentStepId !== INITIAL_STEP_ID && ( // Hide Back button only on first step
            <button
              onClick={handleBack}
              disabled={history.length === 0} // Should not be possible if not on first step, but safe check
              className={`px-4 py-2 text-gray-600 hover:text-gray-800 focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed`}
            >
                <span className="flex items-center">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                  Back
                </span>
            </button>
          )}
          {/* Spacer to keep Start Over on the right when Back is hidden */}
          {currentStepId === INITIAL_STEP_ID && <div className="flex-1"></div>} 

          {currentStepId === 'results' && (
             <button
              onClick={handleReset}
              className="px-4 py-2 text-blue-600 hover:text-blue-800 focus:outline-none"
            >
              Start Over
            </button>
          )}
        </div>
      </div>
    </div>
    </div>
    </div>
  );
};

export default ServiceWizard;
